import { AfterViewInit, Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ValidationErrors } from "@angular/forms";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import { TranslateService } from "@ngx-translate/core";
import {
  AlertController,
  LoadingController,
  NavController,
  NavParams,
  ViewController,
} from "ionic-angular";
import moment from "moment-timezone";

@Component({
  selector: "new-date-time-picker",
  templateUrl: "new-date-time-picker.html",
})
export class NewDateTimePicker implements OnInit, AfterViewInit {
  @Input() group: FormGroup;
  @Input() config;
  @Input() isDate: boolean;
  @Input() isTime: boolean;

  // Internal data model
  id: string;
  componentId: string;
  dateLabel: any;
  timeLabel: any;
  value: string;
  innerGroup: FormGroup;
  disabled: boolean;
  readonly: boolean = false;
  required: boolean;

  spinnerCounter: number = 0;
  spinner: any = null;

  datePlacementPreference: PlacementArray = ["bottom"];
  minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 100));
  minDateFormat = {
    year: this.minDate.getFullYear(),
    month: this.minDate.getMonth() + 1,
    day: this.minDate.getDate(),
  };
  maxDate = new Date();
  maxDateFormat = {
    year: this.maxDate.getFullYear(),
    month: this.maxDate.getMonth() + 1,
    day: this.maxDate.getDate(),
  };

  meridian: boolean = false;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    public translatePipe: TranslateService,
    public formBuilder: FormBuilder
  ) {}

  ngOnInit() {
    let { id, dateLabel, timeLabel, componentId, readonly, rules } =
      this.config;

    this.id = id;
    this.componentId = componentId;
    if (dateLabel !== undefined) {
      this.dateLabel = dateLabel;
    } else {
      this.dateLabel = id;
    }

    if (timeLabel !== undefined) {
      this.timeLabel = timeLabel;
    } else {
      this.timeLabel = id;
    }

    this.readonly = readonly;
    this.value = this.config.default || false;
    if (rules && rules.required) {
      this.required = rules.required;
    }

    this.group.addControl(this.id, this.formBuilder.control({}));
    this.innerGroup = this.formBuilder.group({});
    if (this.isDate) {
      this.innerGroup.addControl("date", this.formBuilder.control({}));
      this.innerGroup.controls["date"].setValue(null);
    }
    if (this.isTime) {
      this.innerGroup.addControl("time", this.formBuilder.control({}));
    }

    this.innerGroup.valueChanges.subscribe((data) => {
      if (this.isDate && this.isTime) {
        this.group.get(this.id).setValue(data, { emitEvent: true });
      }
      this.group.controls[this.id].setErrors(this.getErrors());
    });

    //get the value from outer formgroup and set it inner form group
    this.group.controls[this.id].valueChanges.subscribe((data) => {
      if (data === null) {
        if (this.isDate) {
          this.innerGroup.controls["date"].setValue(null);
        }
        if (this.isTime) {
          this.innerGroup.controls["time"].setValue(null);
        }
      }
    });

    // Set initial value from controls
    if (this.group.controls[this.id] && this.group.controls[this.id].value) {
      if (this.isDate) {
        this.innerGroup.controls["date"].setValue(
          this.group.controls[this.id].value.date,
          { emitEvent: false }
        );
      }
      if (this.isTime) {
        this.innerGroup.controls["time"].setValue(
          this.group.controls[this.id].value.time,
          { emitEvent: false }
        );
      }
    }
  }

  ngAfterViewInit(): void {
    this.group.controls[this.id].setErrors(this.getErrors());

    // Errors can get lost when manually set
    // This is an overzealous approach to guarantee they get maintained
    this.group.valueChanges.subscribe((data) => {
      this.group.controls[this.id].setErrors(this.getErrors());
    });
  }

  getErrors(): ValidationErrors {
    let errors = {};
    if (this.isDate) {
      if (!this.group.get(this.id).value) {
        errors["required"] = true;
      } else if (
        this.group.get(this.id).value &&
        (!this.group.get(this.id).value.date ||
          Object.keys(this.group.get(this.id).value.date).length === 0)
      ) {
        errors["required"] = true;
      }
    }

    if (this.isTime) {
      if (!this.group.get(this.id).value) {
        errors["required"] = true;
      } else if (
        this.group.get(this.id).value &&
        (!this.group.get(this.id).value.time ||
          Object.keys(this.group.get(this.id).value.time).length === 0)
      ) {
        errors["required"] = true;
      }
    }

    if (this.config) {
      if (this.config.rules) {
        if (this.config.rules.closeVisit) {
          errors = this.assessmentCompletionValidation(errors);
        }
      }
    }

    return Object.keys(errors).length == 0 ? undefined : errors;
  }

  assessmentCompletionValidation(errors): any {
    var assessmentStart = this.group.controls["assessmentStart"];
    var assessmentEnd = this.group.controls["assessmentEnd"];

    if (assessmentStart.value && assessmentEnd.value) {
      if (
        assessmentStart.value.date &&
        assessmentStart.value.time &&
        assessmentEnd.value.date &&
        assessmentEnd.value.time
      ) {
        let start = this.formatDate(
          assessmentStart.value.date,
          assessmentStart.value.time
        );
        let end = this.formatDate(
          assessmentEnd.value.date,
          assessmentEnd.value.time
        );

        var mStart = moment(start);
        var mEnd = moment(end);
        var val = mEnd.isSameOrBefore(mStart);

        if (val) errors["dategreater"] = true;

        var duration = moment.duration(mEnd.diff(mStart));
        var hours = duration.asHours();
        if (hours > 8) {
          this.displayConfirmationForVisitDuration();
        }
      }
    }

    return errors;
  }

  private get hasNoSessionKeyForConfirmationDisplay() {
    return !sessionStorage.getItem("key");
  }

  private setSessionKeyForConfirmationDisplay() {
    // This is added to show alert only one time avoid showing multiple times. We set this key whenever
    // we want to display the confirmation for duration. We remove this once the confirmation is displayed.
    sessionStorage.setItem("key", "DatePop");
  }

  private formatDate(date: any, time: any): string {
    return (
      moment(date).local().format("YYYY-MM-DD") +
      "T" +
      this.convertToDoubleDigit(time.hour) +
      ":" +
      this.convertToDoubleDigit(time.minute) +
      ":00"
    );
  }

  private convertToDoubleDigit(n) {
    let temp = n + ""; //Convert to a string
    if (temp.length == 1) {
      return "0" + temp;
    }
    return temp;
  }

  private displayConfirmationForVisitDuration() {
    if (this.hasNoSessionKeyForConfirmationDisplay) {
      this.setSessionKeyForConfirmationDisplay();
      let self = this;
      const alert = this.alertCtrl.create({
        title: this.translatePipe.instant("timegreaterthaneight"),
        buttons: [
          {
            text: "Proceed",
            handler() {
              alert.dismiss().then(() => {
                sessionStorage.removeItem("key");
              });
              return false;
            },
          },
          {
            text: "Cancel",
            handler() {
              alert.dismiss().then(() => {
                sessionStorage.removeItem("key");
                self.group.controls["assessmentStart"].setValue(null, {
                  onlySelf: true,
                });
                self.group.controls["assessmentEnd"].setValue(null, {
                  onlySelf: true,
                });
              });
              return false;
            },
          },
        ],
      });
      setTimeout(() => {
        alert.present();
      }, 200);
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/inputs/new-date-time-picker/new-date-time-picker.ts